// src/context/RequestServiceContext.js
import { createContext, useContext, useEffect, useState } from "react";
import axiosInstance from "../api/axios";
import { useNavigate } from "react-router-dom";
import useAddressContext from "../hooks/useAddressContext";
import useArtisanContext from "../hooks/useArtisanContext";
import useOpenModalContext from "../hooks/useOpenModalContext";
import validate from "validate.js";
import requestFormConstraints from "../components/util/Contraints";
import toast from "react-hot-toast";

const RequestServiceContext = createContext();

export const RequestServiceProvider = ({ children }) => {
  const [requestState, setRequestState] = useState({
    budget: "",
    selectedCategory: {
      id: null,
      name: "",
    },
    description: "",
  });
  const [categories, setCategories] = useState([]);
  const [requestErrors, setRequestErrors] = useState({});
  const [isLoadingRequest, setIsLoadingRequest] = useState(false);
  const { postalCode } = useAddressContext();
  const { setAvailableService } = useArtisanContext();
  const { setOpenRequest, openRequest } = useOpenModalContext();

  /*  explore more */
  const [isLoadingExplore, setIsLoadingExplore] = useState(false);
  const [exploreService, setExploreService] = useState();

  const navigate = useNavigate();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get(
        "/artisan/api/v1/service-category/"
      );
      setCategories(response.data?.data || []);
    } catch (error) {
      console.error("Failed to fetch categories", error);
    }
  };

  const handleChange = (field, value) => {
    setRequestState((prev) => ({ ...prev, [field]: value }));
    setRequestErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleCategorySelect = (id, name) => {
    setRequestState((prev) => ({
      ...prev,
      selectedCategory: { id, name },
    }));
    setRequestErrors((prev) => ({ ...prev, selectedCategory: undefined }));
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    const normalizedPostcode = decodeURIComponent(
      postalCode?.replace(/\s+/g, "")
    );
    const validationData = {
      budget: requestState.budget,
      description: requestState.description,
      selectedCategory: requestState.selectedCategory?.id
        ? requestState.selectedCategory?.name
        : null,
    };

    const validationErrors = validate(validationData, requestFormConstraints);
    if (validationErrors) {
      setRequestErrors(validationErrors);
      return;
    }

    const url = `/service-user/api/v1/available-service/?category_id=${requestState.selectedCategory.id}&postcode=${normalizedPostcode}`;
    const payload = {
      budget: requestState.budget,
      description: requestState.description,
    };

    try {
      setIsLoadingRequest(true);
      const response = await axiosInstance.post(url, payload);
      if (
        response.status === 200 &&
        (response?.data?.data?.length === 0 ||
          response?.data?.data?.services?.length)
      ) {
        toast.error("This service is currently unavailable in your area.");
      } else {
        await setAvailableService(response?.data?.data);
        setOpenRequest(false);
        navigate("/available-providers");
        setRequestState({
          budget: "",
          selectedCategory: { id: null, name: "" },
          description: "",
        });
      }
    } catch (error) {
      console.error("Submit request failed", error);
    } finally {
      setIsLoadingRequest(false);
    }
  };

  useEffect(() => {
    if (!openRequest) {
      setRequestState({
        budget: "",
        selectedCategory: { id: null, name: "" },
        description: "",
      });
    }
  }, [openRequest]);

  const handleExploreMore = async () => {
    const normalizedPostcode = decodeURIComponent(
      postalCode?.replace(/\s+/g, "")
    );
    const url = `/service-user/api/v1/exploremore-service/?category_id=${requestState.selectedCategory.id}&postcode=${normalizedPostcode}`;
    const payload = {
      budget: requestState.budget,
      description: requestState.description,
    };

    try {
      setIsLoadingExplore(true);
      const response = await axiosInstance.post(url, payload);
      if (response.status === 200 || response.status === 201) {
        await setExploreService(response?.data?.data);
      }
    } catch (error) {
      await toast.error("Explore more request failed");
      console.error("Explore more request failed", error);
    } finally {
      setIsLoadingExplore(false);
    }
  };

  return (
    <RequestServiceContext.Provider
      value={{
        requestState,
        setRequestState,
        categories,
        requestErrors,
        isLoadingRequest,
        handleChange,
        handleCategorySelect,
        handleRequestSubmit,
        isLoadingExplore,
        exploreService,
        handleExploreMore,
      }}
    >
      {children}
    </RequestServiceContext.Provider>
  );
};

export const useRequestService = () => useContext(RequestServiceContext);
